<script setup lang="ts">
import { ref } from 'vue';

import init from '@send-frontend/lib/init';

import useKeychainStore from '@send-frontend/stores/keychain-store';
import useUserStore from '@send-frontend/stores/user-store';

import ErrorUploading from '@send-frontend/apps/send/components/ErrorUploading.vue';
import { useUploadAndShare } from '@send-frontend/apps/send/composables/useUploadAndShare';
import useFolderStore from '@send-frontend/apps/send/stores/folder-store';

import BackupAndRestore from '@send-frontend/apps/common/BackupAndRestore.vue';
import {
  ALL_UPLOADS_ABORTED,
  FIFTEEN_MINUTES,
  FILE_LIST,
  MAX_FILE_SIZE,
  POPUP_READY,
} from '@send-frontend/lib/const';
import { ERROR_MESSAGES } from '@send-frontend/lib/errorMessages';
import { restoreKeysUsingLocalStorage } from '@send-frontend/lib/keychain';
import { canUploadQuery } from '@send-frontend/lib/queries';
import useApiStore from '@send-frontend/stores/api-store';
import { useQuery } from '@tanstack/vue-query';
import UploadPage from '../pages/UploadPage.vue';
import { useStatusStore } from '../stores/status-store';
import { useAuth } from '@send-frontend/lib/auth';
import WithLoader from '@send-frontend/apps/common/WithLoader.vue';
import PromptPopupLogin from '@send-frontend/apps/send/views/PromptLogin.vue';
import { useAuthStore } from '@send-frontend/stores';
import { useSendConfig } from '@send-frontend/composables/useSendConfig';

interface FileItem {
  id: number;
  name: string;
  data: File;
}

const userStore = useUserStore();
const { keychain } = useKeychainStore();
const { api } = useApiStore();
const { validators, progress } = useStatusStore();
const { isLoggedIn, refetchAuth, isLoadingAuth } = useAuth();

const folderStore = useFolderStore();
const { isError: uploadingError, uploadAndShare } = useUploadAndShare();

const files = ref<FileItem[] | null>(null);
const message = ref('');

async function handleUploadAndShare(
  files: FileItem[],
  password: string,
  expiration?: string,
  onStatusUpdate?: (
    fileIndex: number,
    status: 'pending' | 'uploading' | 'completed' | 'error'
  ) => void
) {
  if (!files || files.length === 0) {
    return;
  }
  await uploadAndShare(files, password, expiration, onStatusUpdate);
}

const { error: uploadBlockedDuetoSize } = useQuery({
  queryKey: ['can-upload'],
  queryFn: canUploadQuery,
  retry: false,
  staleTime: FIFTEEN_MINUTES,
});

const { data: isConfigured, refetch } = useQuery({
  queryKey: ['is-configured-for-upload'],
  queryFn: async () => {
    await refetchAuth();
    // At the very end we have to validate that everything is in order for the upload to happen
    const { hasBackedUpKeys, isTokenValid, hasForcedLogin } =
      await validators();

    if (!hasBackedUpKeys) {
      message.value = `Please make sure you have backed up or restored your keys. Go back to the compositon panel and follow the instructions`;
      // close this window and open the management page
      return false;
    }
    if (!isTokenValid || hasForcedLogin) {
      message.value = `You're not logged in properly. Please go back to the compositon panel to log back in`;
      return false;
    }
    // If everything is fine, we initialize the app
    await initialize();
    return true;
  },
  refetchOnWindowFocus: true,
  refetchOnMount: true,
});

const initialize = async () => {
  try {
    await restoreKeysUsingLocalStorage(keychain, api);
    await init(userStore, keychain, folderStore);
    console.log(`adding listener in Popup for runtime messages`);

    browser.runtime.onMessage.addListener(async (message) => {
      if (message.type === FILE_LIST) {
        files.value = message.files;
      }
    });

    browser.runtime.sendMessage({
      type: POPUP_READY,
    });
  } catch {
    console.log(
      `Cannot access browser.runtime, probably not running as an extension`
    );
  }

  // Check if the filesize is allowed.
  // Using a for loop so we can return.
  for (let i = 0; i < files.value?.length; i++) {
    const file = files.value[i].data;
    if (file.size > MAX_FILE_SIZE) {
      progress.error = ERROR_MESSAGES.SIZE_EXCEEDED;
      console.log(`Max file size exceeded`);
      uploadingError.value = true;

      browser.runtime.sendMessage({
        type: ALL_UPLOADS_ABORTED,
        url: '',
        aborted: true,
      });

      return;
    }
  }
  // TODO: do this for each file
};
</script>

<template>
  <WithLoader :is-loading="isLoadingAuth">
    <PromptPopupLogin v-if="!isLoggedIn" />
    <div v-else>
      <div v-if="!isConfigured">
        <BackupAndRestore @backup-completed="refetch" />
      </div>
      <div v-else>
        <!-- We only show the error message when storage limit has been exceeded -->
        <h1 v-if="uploadBlockedDuetoSize">{{ uploadBlockedDuetoSize }}</h1>

        <div v-if="!uploadBlockedDuetoSize">
          <div v-if="uploadingError">
            <ErrorUploading />
          </div>

          <div>
            <UploadPage
              :files="files"
              :on-upload-and-share="handleUploadAndShare"
            />
          </div>
        </div>
      </div>
    </div>
  </WithLoader>
</template>
